import { EnumDto, Page, DeliveryStrategyKind } from '@ampsec/platform-client';
import { EnumService } from '@ampsec/platform-client/src/services/rest/EnumService';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import { EngageModal } from 'Components/organisms';
import {
    AMP_ENUMS,
    DEFAULT_ERROR_MSG,
    ENGAGE,
    ENGAGE_SUCCESS_MSG,
    ENGAGE_VARIANT,
    ERROR_VARIANT,
} from 'Core-utils/constants/constants';
import { Employee } from './usersTable';
import { convertFindingViewToNotificationData } from 'Core-utils/helpers/helpers';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { getEnums, postEngageNotifications } from 'Apis/library';
import { ChangeEvent, useContext, useState } from 'react';
import { ActionsBar } from 'Components/molecules';
import { ReactSVG } from 'react-svg';
import EngageIcon from 'Assets/icons/chat.svg';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { useI18n } from 'Hooks/useI18n';
import useTimeSpent from 'Hooks/useTimeSpent';
import { useSearchParamsState } from 'Hooks/useSearchParamsState';
import _ from 'lodash';
import UsersAgGridSection, { type UsersAgGridFilterEnums } from './UsersAgGridSection';

const COMPONENT_IDENTIFIER = 'Users Table';

const filtersTransformer = (dataItems: PromiseSettledResult<Page<EnumDto>>[]) => {
    return dataItems.reduce((prevDataItem, currDataItem) => {
        if (currDataItem.status === 'rejected') {
            return prevDataItem;
        }
        return {
            ...prevDataItem,
            [AMP_ENUMS[currDataItem.value.kind]]: currDataItem.value.data
                .reduce((prevEnum: any[], currEnum) => {
                    return [...prevEnum, ...Object.keys(currEnum)];
                }, [])
                .sort(),
        };
    }, {});
};

const Users = () => {
    useTimeSpent(COMPONENT_IDENTIFIER);
    const { t } = useI18n();
    const org = useSelector((state: RootState) => state.orgModel.org);
    const { addToast } = useContext(ToastsContext);
    const { filter: urlFilter, isUrlLoading, setSearchParamsState, searchParams } = useSearchParamsState();
    const [actionsShow, setActionsShow] = useState<boolean>(true);
    const [selectedRows, setSelectedRows] = useState<Employee[]>([]);
    const [agGridClearVersion, setAgGridClearVersion] = useState(0);
    const [show, setShow] = useState<boolean>(false);
    const [copySecurityTeam, setCopySecurityTeam] = useState<boolean>(false);

    const optionalFields: (keyof EnumService)[] = ['departments', 'organizations', 'titles'];

    const {
        data: enums,
        isLoading: _isEnumsLoading,
        error: _enumsError,
    } = useApiObjectData(optionalFields.join('__'), getEnums, filtersTransformer, { organization: org });

    const enumsData = (enums ?? {}) as { department?: string[]; organization?: string[]; title?: string[] };
    const filterEnums: UsersAgGridFilterEnums = {
        department: Array.isArray(enumsData.department) ? enumsData.department : [],
        organization: Array.isArray(enumsData.organization) ? enumsData.organization : [],
        title: Array.isArray(enumsData.title) ? enumsData.title : [],
    };

    const handleAgGridSelectionChange = (rows: Employee[]) => {
        setSelectedRows(rows);
        if (rows.length > 0) {
            setActionsShow(true);
        }
    };

    const handleActionsClose = () => {
        setSelectedRows([]);
        setAgGridClearVersion((v) => v + 1);
        setActionsShow(false);
    };

    const handleEngageClick = () => setShow((s) => !s);

    const handleClose = () => setShow(false);

    const getToastBody = (variant: string): string => {
        const variantMessages: Record<string, string> = {
            [ENGAGE_VARIANT]: ENGAGE_SUCCESS_MSG,
            [ERROR_VARIANT]: DEFAULT_ERROR_MSG,
        };

        return variantMessages?.[variant] || variant || '';
    };

    const handleCopySecurityTeamCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        setCopySecurityTeam(event.target.checked);
    };

    const handleSend = (text: string, strategy: DeliveryStrategyKind, includeFinding = false) => {
        const NotificationDataArray = selectedRows.map((selectedRow) =>
            convertFindingViewToNotificationData(
                text,
                copySecurityTeam,
                strategy,
                includeFinding,
                undefined,
                selectedRow,
            ),
        );

        postEngageNotifications(NotificationDataArray)
            .then((response) => {
                addToast({
                    severity: response?.error ? 'error' : 'success',
                    body: getToastBody(response?.error || ENGAGE_SUCCESS_MSG),
                });
            })
            .catch((error) => {
                addToast({
                    severity: 'error',
                    body: t(error.response.data.message),
                });
            });
        setShow(false);
        setActionsShow(false);
        setAgGridClearVersion((v) => v + 1);
    };

    const actions = [
        {
            label: ENGAGE,
            icon: (
                <ReactSVG
                    src={EngageIcon}
                    className="data-grid__svg-react-icon d-flex justify-content-center align-items-center"
                />
            ),
            onClick: handleEngageClick,
        },
    ];

    return (
        <div className="h-100 w-100 overflow-visible custom-scrollbar d-flex flex-column">
            <div className="flex-grow-1 min-h-0 d-flex flex-column">
                <UsersAgGridSection
                    filterEnums={filterEnums}
                    urlFilter={urlFilter}
                    isUrlLoading={isUrlLoading}
                    searchParams={searchParams}
                    setSearchParamsState={setSearchParamsState}
                    onSelectedEmployeesChange={handleAgGridSelectionChange}
                    clearSelectionToken={agGridClearVersion}
                />
            </div>
            {selectedRows.length > 0 && actionsShow && (
                <ActionsBar
                    selectedFindings={selectedRows.length}
                    actions={actions}
                    show={selectedRows.length > 0 && actionsShow}
                    onActionsClose={handleActionsClose}
                />
            )}
            {show && (
                <EngageModal
                    show={show}
                    onClose={handleClose}
                    onSend={handleSend}
                    copySecurityTeam={copySecurityTeam}
                    onChangeCheckbox={handleCopySecurityTeamCheckboxChange}
                    findingsData={false}
                />
            )}
        </div>
    );
};

export default Users;
